import React, { useState } from 'react';

const inputStyle = {
  width: '100%', padding: '14px 16px', borderRadius: '10px',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff', fontSize: '14px', outline: 'none',
  transition: 'border-color 0.2s ease', fontFamily: 'DM Sans, sans-serif'
};

export function LoginPage({ onLogin, onSwitchToSignup, onBack }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!form.email || !form.password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://vantage-production-4475.up.railway.app/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem('vantage_token', data.data.token);
        sessionStorage.setItem('vantage_user', JSON.stringify(data.data.user));
        onLogin(data.data.user);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#080e1a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', fontFamily: 'DM Sans, sans-serif'
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Syne:wght@700;800&display=swap');`}</style>

      <div style={{ maxWidth: '440px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '14px',
            background: 'linear-gradient(135deg,#00c853,#1de9b6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '24px', fontWeight: '800', margin: '0 auto 16px',
            fontFamily: 'Syne, sans-serif'
          }}>V</div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: '800', color: '#fff', marginBottom: '8px' }}>
            Welcome Back
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            Sign in to your Vantage account
          </p>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.02)', borderRadius: '20px',
          padding: '40px', border: '1px solid rgba(255,255,255,0.06)'
        }}>
          {error && (
            <div style={{
              padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              fontSize: '13px', color: '#ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8', display: 'block', marginBottom: '8px' }}>
                Email Address
              </label>
              <input style={inputStyle} type="email" placeholder="you@example.com"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                onFocus={e => e.target.style.borderColor = '#00c853'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                onKeyPress={e => e.key === 'Enter' && handleSubmit()} />
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8', display: 'block', marginBottom: '8px' }}>
                Password
              </label>
              <input style={inputStyle} type="password" placeholder="••••••••"
                value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                onFocus={e => e.target.style.borderColor = '#00c853'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                onKeyPress={e => e.key === 'Enter' && handleSubmit()} />
            </div>
          </div>

          <button onClick={handleSubmit} disabled={loading}
            style={{
              width: '100%', padding: '14px', borderRadius: '10px', cursor: 'pointer',
              background: 'linear-gradient(135deg,#00c853,#1de9b6)',
              border: 'none', color: '#000', fontSize: '15px', fontWeight: '800',
              opacity: loading ? 0.7 : 1, transition: 'all 0.2s'
            }}>
            {loading ? 'Signing in...' : 'Sign In →'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#64748b' }}>
            Do not have an account?{' '}
            <span onClick={onSwitchToSignup}
              style={{ color: '#00c853', fontWeight: '700', cursor: 'pointer' }}>
              Sign Up
            </span>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <span onClick={onBack}
            style={{ fontSize: '13px', color: '#64748b', cursor: 'pointer' }}>
            ← Back to Home
          </span>
        </div>
      </div>
    </div>
  );
}

export function SignupPage({ onSignup, onSwitchToLogin, onBack }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password) {
      setError('Please fill in all fields');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://vantage-production-4475.up.railway.app/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password
        })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || 'Signup failed');
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh', background: '#080e1a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', fontFamily: 'DM Sans, sans-serif', textAlign: 'center'
      }}>
        <div style={{ maxWidth: '440px', width: '100%' }}>
          <div style={{ fontSize: '60px', marginBottom: '24px' }}>🎉</div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: '800', color: '#fff', marginBottom: '16px' }}>
            Account Created!
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.7', marginBottom: '32px' }}>
            Your account has been created successfully. To activate your subscription send your payment screenshot to{' '}
            <strong style={{ color: '#00c853' }}>support@vantagestudioai.com</strong>{' '}
            or WhatsApp <strong style={{ color: '#00c853' }}>03114608845</strong> with your email address.
          </p>
          <button onClick={onSwitchToLogin}
            style={{
              padding: '14px 40px', borderRadius: '10px', cursor: 'pointer',
              background: 'linear-gradient(135deg,#00c853,#1de9b6)',
              border: 'none', color: '#000', fontSize: '15px', fontWeight: '800'
            }}>
            Sign In Now →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#080e1a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', fontFamily: 'DM Sans, sans-serif'
    }}>
      <div style={{ maxWidth: '440px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '14px',
            background: 'linear-gradient(135deg,#00c853,#1de9b6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '24px', fontWeight: '800', margin: '0 auto 16px',
            fontFamily: 'Syne, sans-serif'
          }}>V</div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: '800', color: '#fff', marginBottom: '8px' }}>
            Create Account
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            Join Vantage and automate your content
          </p>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.02)', borderRadius: '20px',
          padding: '40px', border: '1px solid rgba(255,255,255,0.06)'
        }}>
          {error && (
            <div style={{
              padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              fontSize: '13px', color: '#ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
            {[
              { key: 'name', label: 'Full Name', type: 'text', placeholder: 'John Doe' },
              { key: 'email', label: 'Email Address', type: 'email', placeholder: 'you@example.com' },
              { key: 'password', label: 'Password', type: 'password', placeholder: '••••••••' },
              { key: 'confirm', label: 'Confirm Password', type: 'password', placeholder: '••••••••' }
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8', display: 'block', marginBottom: '8px' }}>
                  {field.label}
                </label>
                <input style={inputStyle} type={field.type} placeholder={field.placeholder}
                  value={form[field.key]}
                  onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                  onFocus={e => e.target.style.borderColor = '#00c853'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                  onKeyPress={e => e.key === 'Enter' && handleSubmit()} />
              </div>
            ))}
          </div>

          <div style={{
            padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
            background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.2)',
            fontSize: '12px', color: '#64748b', lineHeight: '1.6'
          }}>
            After creating your account send your payment screenshot to activate your subscription. Plans start from Rs 2,000/month.
          </div>

          <button onClick={handleSubmit} disabled={loading}
            style={{
              width: '100%', padding: '14px', borderRadius: '10px', cursor: 'pointer',
              background: 'linear-gradient(135deg,#00c853,#1de9b6)',
              border: 'none', color: '#000', fontSize: '15px', fontWeight: '800',
              opacity: loading ? 0.7 : 1
            }}>
            {loading ? 'Creating Account...' : 'Create Account →'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#64748b' }}>
            Already have an account?{' '}
            <span onClick={onSwitchToLogin}
              style={{ color: '#00c853', fontWeight: '700', cursor: 'pointer' }}>
              Sign In
            </span>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <span onClick={onBack} style={{ fontSize: '13px', color: '#64748b', cursor: 'pointer' }}>
            ← Back to Home
          </span>
        </div>
      </div>
    </div>
  );
}